import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import Hebrew translations
import heUI from './locales/he/ui.json';
import heMetadata from './locales/he/metadata.json';
import heStickers from './locales/he/stickers.json';

// Import English translations
import enUI from './locales/en/ui.json';
import enMetadata from './locales/en/metadata.json';
import enStickers from './locales/en/stickers.json';

// Supported languages configuration
export const supportedLanguages = {
  he: { name: 'עברית', nativeName: 'עברית', dir: 'rtl', flag: '🇮🇱' },
  en: { name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇬🇧' },
} as const;

export type SupportedLanguage = keyof typeof supportedLanguages;

// Check if a language is RTL
export const isRTL = (lang: string): boolean => {
  return lang === 'he' || lang === 'ar';
};

// Get language direction
export const getLanguageDirection = (lang: string): 'rtl' | 'ltr' => {
  return isRTL(lang) ? 'rtl' : 'ltr';
};

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      he: {
        translation: heUI,
        metadata: heMetadata,
        stickers: heStickers,
      },
      en: {
        translation: enUI,
        metadata: enMetadata,
        stickers: enStickers,
      },
    },
    fallbackLng: 'he',
    supportedLngs: Object.keys(supportedLanguages),
    load: 'languageOnly', // e.g. 'en-US' → 'en', so navigator detection matches supported languages
    
    // Detection options
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'talk-game-language',
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Default namespace
    defaultNS: 'translation',
    ns: ['translation', 'metadata', 'stickers'],
  });

// Update document direction and language when language changes
i18n.on('languageChanged', (lng) => {
  const dir = getLanguageDirection(lng);
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
  
  // Store the language preference
  localStorage.setItem('talk-game-language', lng);
});

// Set initial direction based on current language
const currentLang = i18n.language || 'he';
document.documentElement.dir = getLanguageDirection(currentLang);
document.documentElement.lang = currentLang;

export default i18n;
